import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Text,
  Flex,
  HStack,
  Select,
  Badge,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
} from "@chakra-ui/react";
import React from "react";
import ReactPaginate from "react-paginate";
import { BiChevronDown, BiChevronLeft, BiChevronRight } from "react-icons/bi";
import { BsThreeDotsVertical } from "react-icons/bs";
import Loader from "../Loader/loader";
import { formatMoney } from "../../services/utils/helpersFunc";
import { motion, AnimatePresence } from "framer-motion";

const MotionTr = motion(Tr);

const HerVestTable = ({
  columns,
  data,
  meta,
  itemsPerPage = 10,
  handlePageClick,
  setItemsPerPage,
  onClickHandler,
  loading,
}) => {
  const renderStatus = (status) => {
    const s = status?.toLowerCase();
    let colorScheme = "gray";
    let variant = "subtle";

    if (s === "pending") colorScheme = "blue";
    else if (s === "paid" || s === "active") colorScheme = "green";
    else if (s === "retrying") colorScheme = "orange";
    else if (s === "rejected" || s === "declined" || s === "failed") colorScheme = "red";
    else if (s === "closed") colorScheme = "purple";

    return (
      <Badge
        px={3}
        py={1}
        borderRadius="full"
        textTransform="capitalize"
        colorScheme={colorScheme}
        variant={variant}
        fontSize="11px"
        fontWeight="600"
      >
        {status}
      </Badge>
    );
  };

  const totalItems = meta?.totalItems || data?.length || 0;
  const currentPage = meta?.currentPage ? meta.currentPage - 1 : 0;
  const startRange = currentPage * itemsPerPage + 1;
  const endRange = Math.min((currentPage + 1) * itemsPerPage, totalItems);

  return (
    <Box bg="white" borderRadius="xl" boxShadow="sm" border="1px solid" borderColor="gray.100" overflow="hidden">
      <TableContainer>
        <Table variant="simple">
          <Thead bg="gray.50">
            <Tr>
              {columns.map((column, index) => (
                <Th
                  key={index}
                  textTransform="uppercase"
                  fontSize="11px"
                  color="gray.500"
                  letterSpacing="wider"
                  py={4}
                  fontWeight="700"
                >
                  {column.name}
                </Th>
              ))}
              <Th></Th>
            </Tr>
          </Thead>

          <Tbody>
            <AnimatePresence mode="wait">
              {!loading && data?.map((item, i) => (
                <MotionTr
                  key={i}
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2, delay: i * 0.03 }}
                  cursor="pointer"
                  _hover={{ bg: "gray.50" }}
                  onClick={() => onClickHandler(item)}
                >
                  {columns.map((column, colIndex) => {
                    const value = item[column.key];
                    return (
                      <Td
                        key={colIndex}
                        fontSize="13px"
                        color="gray.700"
                        py={4}
                        fontWeight="500"
                      >
                        {column.key === "amount" ? (
                          <Text fontWeight="600" color="gray.900">
                            ₦{formatMoney(value)}
                          </Text>
                        ) : column.key === "status" || column.key === "loan_status" ? (
                          renderStatus(value)
                        ) : (
                          value || "---"
                        )}
                      </Td>
                    );
                  })}
                  <Td onClick={(e) => e.stopPropagation()}>
                    <Menu isLazy>
                      <MenuButton
                        as={IconButton}
                        icon={<BsThreeDotsVertical />}
                        variant="ghost"
                        size="sm"
                        borderRadius="full"
                        _hover={{ bg: "gray.100" }}
                      />
                      <MenuList borderRadius="md" shadow="lg" py={2}>
                        <MenuItem onClick={() => onClickHandler(item)} fontSize="13px" fontWeight="500">View Details</MenuItem>
                      </MenuList>
                    </Menu>
                  </Td>
                </MotionTr>
              ))}
            </AnimatePresence>
          </Tbody>
        </Table>
      </TableContainer>

      {loading && (
        <Flex justify="center" align="center" py={20} direction="column" gap={4}>
          <Loader />
          <Text fontSize="sm" color="gray.500" fontWeight="500">Loading data...</Text>
        </Flex>
      )}

      {!loading && (!data || data.length === 0) && (
        <Flex justify="center" align="center" py={20} direction="column" gap={2}>
          <Text fontWeight="600" color="gray.800">No results found</Text>
          <Text fontSize="sm" color="gray.500">We couldn't find any data matching your request.</Text>
        </Flex>
      )}

      {data?.length > 0 && (
        <Flex
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          bg="white"
          p="16px 24px"
          borderTop="1px solid"
          borderColor="gray.100"
        >
          <HStack spacing={4}>
            <Select
              icon={<BiChevronDown />}
              bg="gray.50"
              border="1px solid"
              borderColor="gray.200"
              borderRadius="md"
              color="gray.700"
              fontWeight="600"
              fontSize="13px"
              w="110px"
              h="36px"
              cursor="pointer"
              _hover={{ borderColor: "brand.500" }}
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
              }}
            >
              <option value={10}>10 / page</option>
              <option value={20}>20 / page</option>
              <option value={50}>50 / page</option>
              <option value={100}>100 / page</option>
            </Select>

            <Text
              color="gray.600"
              fontWeight="500"
              fontSize="13px"
            >
              Showing {totalItems > 0 ? `${startRange}-${endRange}` : "0"} of {totalItems} Entries
            </Text>
          </HStack>

          <Box className="pagination-wrapper">
            <ReactPaginate
              breakLabel="..."
              nextLabel={<BiChevronRight size={20} />}
              onPageChange={handlePageClick}
              pageRangeDisplayed={3}
              marginPagesDisplayed={2}
              pageCount={meta?.totalPages || 1}
              previousLabel={<BiChevronLeft size={20} />}
              containerClassName="pagination-container"
              pageClassName="page-item"
              pageLinkClassName="page-link"
              previousClassName="page-item prev"
              previousLinkClassName="page-link"
              nextClassName="page-item next"
              nextLinkClassName="page-link"
              breakClassName="page-item"
              breakLinkClassName="page-link"
              activeClassName="active"
              disabledClassName="disabled"
            />
          </Box>
        </Flex>
      )}
    </Box>
  );
};

export default HerVestTable;
